import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export interface ConfirmModalProps {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'default'
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
  children?: ReactNode
}

/**
 * Reusable confirmation dialog for destructive / irreversible actions
 * (logout, order cancel, etc.). Fully keyboard-accessible.
 */
export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  loading = false,
  onConfirm,
  onCancel,
  children,
}: ConfirmModalProps) {
  const { t } = useTranslation('common')
  const titleId = useId()
  const descId = useId()
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const prev = document.activeElement as HTMLElement | null
    confirmRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
      prev?.focus?.()
    }
  }, [open, onCancel])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label={cancelLabel ?? t('actions.cancel')}
        onClick={onCancel}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={cn(
          'relative w-full max-w-sm rounded border border-terminal-border',
          'bg-terminal-surface p-5 shadow-2xl',
          'animate-[modal-in_180ms_ease-out]',
        )}
      >
        <h2 id={titleId} className="text-base font-semibold text-terminal-fg">
          {title}
        </h2>
        <p id={descId} className="mt-2 text-sm leading-relaxed text-terminal-muted">
          {message}
        </p>
        {children}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className={cn(
              'h-9 min-w-20 rounded px-3 text-sm font-medium',
              'border border-terminal-border bg-transparent text-terminal-fg',
              'hover:bg-terminal-elevated disabled:opacity-50',
            )}
          >
            {cancelLabel ?? t('actions.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'h-9 min-w-20 rounded px-3 text-sm font-semibold text-white',
              'disabled:opacity-50',
              tone === 'danger'
                ? 'bg-danger hover:bg-danger-muted'
                : 'bg-accent hover:bg-accent-muted',
            )}
          >
            {loading
              ? t('actions.working')
              : (confirmLabel ?? t('actions.confirm'))}
          </button>
        </div>
      </div>
    </div>
  )
}
